/**
 * Patient grade client: badges are fetched in batches (every badge on a
 * schedule day asks within the same tick, one request answers them all),
 * cached per patient under `['patient_grade', id]` so a `patient_grade` live
 * event refetches them.
 */
import {
  GradeCriteriaApplySchema,
  GradeCriteriaPreviewSchema,
  GradeCriteriaViewSchema,
  GradeDetailSchema,
  GradeListSchema,
  GradePolicySchema,
  GradeDistributionSchema,
  GradeExplanationSchema,
  GradeLettersSchema,
  GradeOverviewSchema,
  PatientGradeSchema,
  PolicyEvaluationSchema,
  PolicyMessageResultSchema,
  type GradeCriteria,
  type GradeLetterValue,
  type GradeOverviewQuery,
  type GradePolicyPut,
  type PatientGrade,
  type PolicyEvaluateBody,
} from '@velora/contracts/patient-grade';
import { useQuery } from '@tanstack/react-query';
import { clientFor, currentOfficeClient, useLive, usePermission } from '../../../app/shell.js';

const B = '/patient-grade';
export const gradeKey = (id: string) => ['patient_grade', id] as const;

type Waiter = { resolve: (g: PatientGrade | null) => void; reject: (e: unknown) => void };
const pending = new Map<string, Waiter[]>();
let timer: ReturnType<typeof setTimeout> | null = null;

async function flush(): Promise<void> {
  timer = null;
  const batch = [...pending.entries()].slice(0, 200);
  for (const [id] of batch) pending.delete(id);
  if (pending.size > 0) timer = setTimeout(() => void flush(), 0);
  const ids = batch.map(([id]) => id);
  try {
    const res = await currentOfficeClient().request(`${B}/grades?patientIds=${ids.join(',')}`, {
      schema: GradeListSchema,
    });
    const by = new Map(res.grades.map((g) => [g.patientId.toLowerCase(), g]));
    for (const [id, ws] of batch) for (const w of ws) w.resolve(by.get(id) ?? null);
  } catch (err) {
    for (const [, ws] of batch) for (const w of ws) w.reject(err);
  }
}

/** One patient's grade, batched with every other badge asking in the same tick. */
export function fetchGrade(patientId: string): Promise<PatientGrade | null> {
  const id = patientId.toLowerCase();
  return new Promise((resolve, reject) => {
    const list = pending.get(id) ?? [];
    list.push({ resolve, reject });
    pending.set(id, list);
    timer ??= setTimeout(() => void flush(), 10);
  });
}

/** The badge data; null while loading, without permission or for no patient. */
export function usePatientGrade(patientId: string | null | undefined): PatientGrade | null {
  const canView = usePermission('patient.view');
  useLive('patient_grade');
  const q = useQuery({
    queryKey: gradeKey(patientId ?? ''),
    queryFn: () => fetchGrade(patientId ?? ''),
    enabled: Boolean(patientId) && canView,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  return q.data ?? null;
}

export const gradeApi = {
  detail: (id: string) =>
    currentOfficeClient().request(`${B}/patients/${id}`, { schema: GradeDetailSchema }),
  override: (id: string, b: { letter: string; reason: string; expiresOn?: string }) =>
    currentOfficeClient().request(`${B}/patients/${id}/override`, {
      method: 'POST',
      body: b,
      schema: PatientGradeSchema,
    }),
  restore: (id: string, reason: string) =>
    currentOfficeClient().request(`${B}/patients/${id}/restore`, {
      method: 'POST',
      body: { reason },
      schema: PatientGradeSchema,
    }),
  clear: (id: string, reason: string) =>
    currentOfficeClient().request(`${B}/patients/${id}/override/clear`, {
      method: 'POST',
      body: { reason },
      schema: PatientGradeSchema,
    }),
  excuse: (b: {
    patientId: string;
    source: string;
    sourceId: string;
    category: string;
    reason: string;
  }) =>
    currentOfficeClient().request(`${B}/excusals`, {
      method: 'POST',
      body: b,
      schema: PatientGradeSchema,
    }),
  revoke: (id: string, reason: string) =>
    currentOfficeClient().request(`${B}/excusals/${id}/revoke`, {
      method: 'POST',
      body: { reason },
      schema: PatientGradeSchema,
    }),
  policy: (locationId: string) =>
    clientFor(locationId).request(`${B}/policy?locationId=${locationId}`, {
      schema: GradePolicySchema,
    }),
  savePolicy: (b: GradePolicyPut) =>
    clientFor(b.locationId).request(`${B}/policy`, {
      method: 'PUT',
      body: b,
      schema: GradePolicySchema,
    }),
  evaluate: (b: PolicyEvaluateBody) =>
    clientFor(b.locationId).request(`${B}/policy/evaluate`, {
      method: 'POST',
      body: b,
      schema: PolicyEvaluationSchema,
    }),
  message: (b: { patientId: string; locationId: string; rule: string; appointmentId?: string }) =>
    clientFor(b.locationId).request(`${B}/policy/message`, {
      method: 'POST',
      body: b,
      schema: PolicyMessageResultSchema,
    }),
  distribution: (locationId?: string) =>
    (locationId ? clientFor(locationId) : currentOfficeClient()).request(
      `${B}/distribution${locationId ? `?locationId=${locationId}` : ''}`,
      { schema: GradeDistributionSchema },
    ),
  /** The practice's grade rules (practice-wide; the office selector does not apply). */
  criteria: () =>
    currentOfficeClient().request(`${B}/criteria`, { schema: GradeCriteriaViewSchema }),
  saveCriteria: (criteria: GradeCriteria) =>
    currentOfficeClient().request(`${B}/criteria`, {
      method: 'PUT',
      body: { criteria },
      schema: GradeCriteriaViewSchema,
    }),
  /** A-F counts now vs. under these criteria (counts only). */
  previewCriteria: (criteria: GradeCriteria) =>
    currentOfficeClient().request(`${B}/criteria/preview`, {
      method: 'POST',
      body: { criteria },
      schema: GradeCriteriaPreviewSchema,
    }),
  applyCriteria: () =>
    currentOfficeClient().request(`${B}/criteria/apply`, {
      method: 'POST',
      schema: GradeCriteriaApplySchema,
    }),
  /** "Why this grade" (an audited PHI read of this patient). */
  explain: (id: string) =>
    currentOfficeClient().request(`${B}/patients/${id}/explain`, {
      schema: GradeExplanationSchema,
    }),
  letters: (patientIds: readonly string[]) =>
    currentOfficeClient().request(`${B}/letters`, {
      method: 'POST',
      body: { patientIds },
      schema: GradeLettersSchema,
    }),
  overview: (q: Partial<GradeOverviewQuery>) => {
    const sp = new URLSearchParams();
    if (q.days) sp.set('days', String(q.days));
    if (q.to) sp.set('to', q.to);
    if (q.locationId) sp.set('locationId', q.locationId);
    const qs = sp.toString();
    return currentOfficeClient().request(`${B}/overview${qs ? `?${qs}` : ''}`, {
      schema: GradeOverviewSchema,
    });
  },
};

/** The popover's explanation; fetched only while it is open. */
export function useGradeExplanation(patientId: string, open: boolean) {
  return useQuery({
    queryKey: ['patient_grade', patientId, 'explain'],
    queryFn: () => gradeApi.explain(patientId),
    enabled: open && Boolean(patientId),
    staleTime: 60_000,
    retry: false,
  });
}

const LETTER_CHUNK = 5000;

/** A short key for a (sorted) id list, so the cache key stays small. */
function hashIds(ids: readonly string[]): string {
  let h = 5381;
  for (const id of ids)
    for (let i = 0; i < id.length; i++) h = ((h << 5) + h + id.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/**
 * Letters for a whole list (recall, campaigns), for the grade filter. Only
 * asked while a filter is on: `enabled` false costs nothing. Never-graded
 * patients read as B.
 */
export function useGradeLetters(
  patientIds: readonly string[],
  enabled: boolean,
): { letters: ReadonlyMap<string, GradeLetterValue> | null; loading: boolean } {
  const canView = usePermission('patient.view');
  const ids = [...new Set(patientIds.map((x) => x.toLowerCase()))].sort();
  const q = useQuery({
    queryKey: ['patient_grade', 'letters', ids.length, hashIds(ids)],
    queryFn: async () => {
      const out = new Map<string, GradeLetterValue>();
      for (let i = 0; i < ids.length; i += LETTER_CHUNK) {
        const r = await gradeApi.letters(ids.slice(i, i + LETTER_CHUNK));
        for (const [k, v] of Object.entries(r.letters)) out.set(k.toLowerCase(), v);
      }
      return out;
    },
    enabled: enabled && canView && ids.length > 0,
    staleTime: 5 * 60_000,
  });
  return { letters: q.data ?? null, loading: q.isFetching };
}
